import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CodeIcon from '@mui/icons-material/Code';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import LinkIcon from '@mui/icons-material/Link';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { addComment, createPortalPost, deleteComment, deletePortalPost, getFeeds, getLikeStatus, getMyPortalPosts, getPortalPosts, getYoutubeVideoURL, likePost, listComments, unlikePost, updatePortalPost, uploadFeedFeatureFile } from '../../Utils/apiService';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { enqueueSnackbar } from 'notistack';

interface MediaItem {
  url: string;
  type: string;
  thumbnailUrl?: string;
  name?: string;
  fileId?: string;
  uploading?: boolean;
  uploadError?: boolean;
}

interface CommentItem {
  id: string;
  authorId: string;
  authorUserName: string;
  authorProfileUrl?: string;
  content: string;
  createdAt: string;
}

interface FeedItem {
  id?: string;
  userImage: string;
  title: string;
  date: string;
  mediaLogo: string;
  content: string;
  contentHtml?: string;
  hashtags: string;
  media: MediaItem[];
  isMine?: boolean;
  status?: string;
  isPendingApproval?: boolean;
  isRejected?: boolean;
}

const getUploadedFileData = (response: any) => response?.data ?? response;
const getPostStatus = (item: any) => item.status || item.approvalStatus || item.approval_status || item.moderationStatus || item.reviewStatus || '';
const isPendingPost = (status?: string) => Boolean(status && status.toLowerCase().includes('pending'));
const isRejectedPost = (status?: string) => Boolean(status && status.toLowerCase().includes('rejected'));
const getAuthorProfileUrl = (item: any) =>
  item.authorProfileUrl ||
  item.authorProfileURL ||
  item.author_profile_url ||
  item.profileUrl ||
  item.profileURL ||
  item.author?.profileUrl ||
  item.author?.profileURL;
const getPostDateValue = (item: any) =>
  item.publishedAt ||
  item.createdAt ||
  item.created_at ||
  item.updatedAt ||
  item.updated_at ||
  item.submittedAt ||
  item.timestamp;

const formatPostDateTime = (value?: string | number | Date) => {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;

  return safeDate.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatPostDate = (value?: string | number | Date) => {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;

  return safeDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const HEADER_FAVICON_LOGO = 'https://iimustorageprod.blob.core.windows.net/iimu-data/KISLogo.png';

const emojiOptions = ['😀', '😊', '😍', '🎉', '👏', '🙏', '✨', '❤️', '👍', '🔥', '💡', '📣'];

const editorTools: Array<{
  label: string;
  icon: typeof FormatBoldIcon;
  className: string;
  value?: string;
}> = [
  { label: 'Bold', icon: FormatBoldIcon, className: 'ql-bold' },
  { label: 'Italic', icon: FormatItalicIcon, className: 'ql-italic' },
  { label: 'Underline', icon: FormatUnderlinedIcon, className: 'ql-underline' },
  { label: 'Strikethrough', icon: FormatStrikethroughIcon, className: 'ql-strike' },
  { label: 'Bulleted list', icon: FormatListBulletedIcon, className: 'ql-list', value: 'bullet' },
  { label: 'Numbered list', icon: FormatListNumberedIcon, className: 'ql-list', value: 'ordered' },
  { label: 'Align left', icon: FormatAlignLeftIcon, className: 'ql-align', value: '' },
];

function linkify(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#1976d2;text-decoration:underline;">${url}</a>`;
  });
}

const MediaCarousel: React.FC<{ media: MediaItem[] }> = ({ media }) => {
  const [current, setCurrent] = useState(0);

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent((c) => (c - 1 + media.length) % media.length);
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrent((c) => (c + 1) % media.length);
  };

  const item = media[current];
  const hasMultiple = media.length > 1;

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 2,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#0a0a0a',
        userSelect: 'none',
      }}
    >
      {item.type === 'photo' && (
        <Box
          component="img"
          src={item.url}
          alt={`media-${current}`}
          sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block' }}
        />
      )}
      {(item.type === 'video' || item.type === 'animated_gif') && (
        <video
          key={item.url}
          src={item.url}
          controls={item.type === 'video'}
          autoPlay={item.type === 'animated_gif'}
          loop={item.type === 'animated_gif'}
          muted={item.type === 'animated_gif'}
          playsInline
          poster={item.thumbnailUrl}
          style={{ width: '100%', maxHeight: 480, display: 'block' }}
        />
      )}

      {hasMultiple && (
        <>
          <IconButton
            onClick={prev}
            size="small"
            sx={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              width: 34,
              height: 34,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.80)' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={next}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              width: 34,
              height: 34,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.80)' },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 12,
              backgroundColor: 'rgba(0,0,0,0.52)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              px: 1.25,
              py: 0.4,
              borderRadius: '999px',
              lineHeight: 1.5,
            }}
          >
            {current + 1} / {media.length}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            {media.map((_, idx) => (
              <Box
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
                sx={{
                  width: idx === current ? 18 : 8,
                  height: 8,
                  borderRadius: '999px',
                  backgroundColor: idx === current ? '#fff' : 'rgba(255,255,255,0.45)',
                  cursor: 'pointer',
                  transition: 'width 180ms ease, background-color 180ms ease',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

interface FeedProps {
  hideComposer?: boolean;
}

const Feed: React.FC<FeedProps> = ({ hideComposer = false }) => {
  const {
    state: { user },
  } = useContext(AuthContext);
  const [feedData, setFeedData] = useState<FeedItem[]>([]);
  const [portalPosts, setPortalPosts] = useState<FeedItem[]>([]);
  const [portalCursor, setPortalCursor] = useState<string | undefined>();
  const [portalPostsLoading, setPortalPostsLoading] = useState(false);
  const [portalPostsHasMore, setPortalPostsHasMore] = useState(true);
  const [portalPostSaving, setPortalPostSaving] = useState(false);
  const [myPosts, setMyPosts] = useState<FeedItem[]>([]);
  const [myPostsPage, setMyPostsPage] = useState(1);
  const [myPostsLoading, setMyPostsLoading] = useState(false);
  const [myPostsHasMore, setMyPostsHasMore] = useState(true);
  const [youtubeVideoURL, setYoutubeVideoURL] = useState<any>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTitle, setComposerTitle] = useState('');
  const [composerValue, setComposerValue] = useState('');
  const [composerHtml, setComposerHtml] = useState('');
  const [attachments, setAttachments] = useState<MediaItem[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [likeStatus, setLikeStatus] = useState<Record<string, { liked: boolean; count: number }>>({});
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentsByPostId, setCommentsByPostId] = useState<Record<string, CommentItem[]>>({});
  const [commentsLoadingById, setCommentsLoadingById] = useState<Record<string, boolean>>({});
  const [commentHasMoreById, setCommentHasMoreById] = useState<Record<string, boolean>>({});
  const [commentPageById, setCommentPageById] = useState<Record<string, number>>({});
  const [commentInput, setCommentInput] = useState<Record<string, string>>({});
  const [commentSubmittingById, setCommentSubmittingById] = useState<Record<string, boolean>>({});

  const [postMenuAnchor, setPostMenuAnchor] = useState<null | HTMLElement>(null);
  const [postMenuTarget, setPostMenuTarget] = useState<FeedItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeedItem | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const quillRef = useRef<ReactQuill | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const portalPostsLoadingRef = useRef(false);
  const myPostsLoadingRef = useRef(false);
  const likeStatusFetchedRef = useRef<Set<string>>(new Set());
  const isMobile = useMediaQuery('(max-width:900px)');

  const displayName = useMemo(() => {
    return user?.applicantFirstName || user?.applicantName?.split(' ')?.[0] || 'there';
  }, [user?.applicantFirstName, user?.applicantName]);
  const userAvatar = user?.Profile_Picture_URL__c || HEADER_FAVICON_LOGO;

  const accessToken = user?.accessToken?.token;
  const visiblePosts = activeTab === 0 ? feedData : activeTab === 1 ? portalPosts : myPosts;

  const formatPortalPosts = (posts: any[]): FeedItem[] =>
    posts.map((item: any) => {
      const formattedDate = formatPostDateTime(getPostDateValue(item));
      const status = getPostStatus(item);

      const rawFiles: any[] = Array.isArray(item.files)
        ? item.files
        : item.file
        ? [item.file]
        : [];

      const media: MediaItem[] = rawFiles
        .map((file: any) => {
          const url = file.publicUrl || file.url || file.fileUrl || '';
          const contentType = (file.mediaType || file.contentType || file.mimeType || '').toLowerCase();
          const type: MediaItem['type'] = contentType.startsWith('video') ? 'video' : 'photo';
          return { url, type, name: file.originalFileName || file.name || '', fileId: file.fileId || file.id };
        })
        .filter((m) => Boolean(m.url));

      return {
        id: item.id,
        userImage: getAuthorProfileUrl(item) || HEADER_FAVICON_LOGO,
        title: item.title || 'Portal Post',
        date: formattedDate,
        mediaLogo: HEADER_FAVICON_LOGO,
        content: item.content || '',
        hashtags: ((item.content || '').match(/#[\w]+/g) || []).join(' '),
        media,
        status,
        isPendingApproval: isPendingPost(status),
        isRejected: isRejectedPost(status),
      };
    });

  const loadPortalPosts = useCallback(
    async (cursor?: string) => {
      if (!accessToken || portalPostsLoadingRef.current) return;

      portalPostsLoadingRef.current = true;
      setPortalPostsLoading(true);
      try {
        const res = await getPortalPosts(accessToken, cursor);
        const posts = res?.data?.data || [];
        const formattedPosts = Array.isArray(posts) ? formatPortalPosts(posts) : [];

        setPortalPosts((currentPosts) => (cursor ? [...currentPosts, ...formattedPosts] : formattedPosts));
        setPortalCursor(res?.data?.nextPage || undefined);
        setPortalPostsHasMore(Boolean(res?.data?.nextPage));
      } catch (error) {
        console.error('Portal posts load failed:', error);
      } finally {
        portalPostsLoadingRef.current = false;
        setPortalPostsLoading(false);
      }
    },
    [accessToken]
  );

  const loadMyPosts = useCallback(
    async (page = 1) => {
      if (!accessToken || myPostsLoadingRef.current) return;

      myPostsLoadingRef.current = true;
      setMyPostsLoading(true);
      try {
        const res = await getMyPortalPosts(accessToken, page, 10);
        const responseData = res?.data;
        const posts = responseData?.data || responseData?.posts || [];
        const formattedPosts = Array.isArray(posts) ? formatPortalPosts(posts) : [];
        const totalPages = responseData?.totalPages || responseData?.pagination?.totalPages;
        const nextPage = responseData?.nextPage || responseData?.pagination?.nextPage;
        const hasMore = nextPage ? true : totalPages ? page < totalPages : formattedPosts.length === 10;

        setMyPosts((currentPosts) => (page > 1 ? [...currentPosts, ...formattedPosts] : formattedPosts));
        setMyPostsPage(nextPage || page + 1);
        setMyPostsHasMore(hasMore);
      } catch (error) {
        console.error('My posts load failed:', error);
      } finally {
        myPostsLoadingRef.current = false;
        setMyPostsLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    getYoutubeVideoURL().then((res) => {
      setYoutubeVideoURL(res?.data?.records?.[0]);
    });

    getFeeds().then((res) => {
      const apiData = res?.data?.insta || [];

      const formattedData = apiData.map((item: any) => {
        const formattedDate = formatPostDate(getPostDateValue(item));
        const hashtags = (item.caption?.match(/#[\w]+/g) || []).join(' ');
        const mediaItems: MediaItem[] = [];

        if (item.media_url) {
          mediaItems.push({
            url: item.media_url,
            type: item.media_type === 'VIDEO' ? 'video' : 'photo',
            thumbnailUrl: item.thumbnail_url,
          });
        }

        return {
          userImage: HEADER_FAVICON_LOGO,
          title: 'Kodaischoolalumni',
          date: formattedDate,
          mediaLogo: 'https://iimustorageprod.blob.core.windows.net/iimu-data/instagramLogo.png',
          content: item.caption || '',
          hashtags,
          media: mediaItems,
        };
      });

      setFeedData(formattedData);
    });
  }, []);

  useEffect(() => {
    if (!accessToken) return;

    setPortalPosts([]);
    setPortalCursor(undefined);
    setPortalPostsHasMore(true);
    setMyPosts([]);
    setMyPostsPage(1);
    setMyPostsHasMore(true);
    loadPortalPosts();
    loadMyPosts(1);
  }, [accessToken, loadMyPosts, loadPortalPosts]);

  useEffect(() => {
    if (!accessToken) return;
    const ids = [...portalPosts, ...myPosts]
      .map((p) => p.id)
      .filter((id): id is string => typeof id === 'string' && !likeStatusFetchedRef.current.has(id));
    const unique = [...new Set(ids)];
    if (!unique.length) return;
    unique.forEach((id) => likeStatusFetchedRef.current.add(id));
    unique.forEach((id) => {
      getLikeStatus(id, accessToken)
        .then((res) => {
          const payload = res?.data ?? res;
          setLikeStatus((prev) => ({ ...prev, [id]: { liked: payload?.liked ?? false, count: payload?.count ?? 0 } }));
        })
        .catch(() => {
          setLikeStatus((prev) => ({ ...prev, [id]: { liked: false, count: 0 } }));
        });
    });
  }, [portalPosts, myPosts, accessToken]);

  const handlePostsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (activeTab === 1 && portalPostsHasMore && !portalPostsLoading && portalCursor && distanceFromBottom < 160) {
      loadPortalPosts(portalCursor);
    }

    if (activeTab === 2 && myPostsHasMore && !myPostsLoading && distanceFromBottom < 160) {
      loadMyPosts(myPostsPage);
    }
  };

  const handleCancelPost = () => {
    setComposerTitle('');
    setComposerValue('');
    setComposerHtml('');
    attachments.forEach((attachment) => URL.revokeObjectURL(attachment.url));
    setAttachments([]);
    setComposerOpen(false);
    setShowEmojiPicker(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const getQuill = useCallback(() => quillRef.current?.getEditor(), []);

  const focusEditor = useCallback(() => {
    quillRef.current?.focus();
  }, []);

  const getPlainText = (editor: any) => editor.getText().replace(/\n$/, '');

  const syncComposer = (html: string, editor: any) => {
    const plainText = getPlainText(editor);
    if (plainText.length > 4000) {
      getQuill()?.deleteText(4000, plainText.length);
      return;
    }
    setComposerHtml(html === '<p><br></p>' ? '' : html);
    setComposerValue(plainText);
  };

  const insertText = useCallback((text: string) => {
    const editor = getQuill();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range?.index ?? editor.getLength(), text, 'user');
    editor.setSelection((range?.index ?? editor.getLength()) + text.length, 0);
    focusEditor();
  }, [focusEditor, getQuill]);

  const handleLink = useCallback(function handleLink(this: any) {
    const editor = getQuill();
    if (!editor) return;
    const url = window.prompt('Enter link URL');
    if (!url) return;
    const range = editor.getSelection(true);
    if (range && range.length > 0) {
      editor.format('link', url, 'user');
      return;
    }
    editor.insertText(range?.index ?? editor.getLength(), url, 'link', url, 'user');
    editor.setSelection((range?.index ?? editor.getLength()) + url.length, 0);
  }, [getQuill]);

  const handleInsertCode = () => {
    const editor = getQuill();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range?.index ?? editor.getLength(), 'code', { code: true }, 'user');
    editor.setSelection((range?.index ?? editor.getLength()) + 4, 0);
    focusEditor();
  };

  const handleMention = () => {
    insertText('@');
  };

  const handleEmoji = (emoji: string) => {
    insertText(emoji);
    setShowEmojiPicker(false);
  };

  const handleImage = useCallback(function handleImage(this: any) {
    imageInputRef.current?.click();
  }, []);

  const handleVideo = useCallback(function handleVideo(this: any) {
    videoInputRef.current?.click();
  }, []);

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];

  const handleLocalMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'photo' | 'video') => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const allowedTypes = type === 'photo' ? ALLOWED_IMAGE_TYPES : ALLOWED_VIDEO_TYPES;
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    const rejectedFiles = files.filter((file) => !allowedTypes.includes(file.type));

    if (rejectedFiles.length > 0) {
      const names = rejectedFiles.map((f) => f.name).join(', ');
      enqueueSnackbar(`Unsupported format: ${names}. Allowed: ${type === 'photo' ? 'JPEG, PNG, GIF, WebP' : 'MP4, WebM, MOV'}`, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }

    if (!validFiles.length) {
      event.target.value = '';
      return;
    }

    const selectedMedia = validFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type,
      name: file.name,
      uploading: true,
    }));

    setAttachments((current) => [...current, ...selectedMedia]);
    event.target.value = '';

    if (!accessToken) {
      setAttachments((current) =>
        current.map((attachment) =>
          selectedMedia.some((media) => media.url === attachment.url)
            ? { ...attachment, uploading: false, uploadError: true }
            : attachment
        )
      );
      return;
    }

    await Promise.all(
      selectedMedia.map(async (media, index) => {
        try {
          const response = await uploadFeedFeatureFile(validFiles[index], accessToken);
          const uploadedFile = getUploadedFileData(response);
          const uploadedFileId = uploadedFile?.id || uploadedFile?.fileId;

          if (!uploadedFileId) {
            throw new Error('Upload response did not include a file id');
          }

          setAttachments((current) =>
            current.map((attachment) =>
              attachment.url === media.url
                ? {
                    ...attachment,
                    fileId: uploadedFileId,
                    url: uploadedFile?.publicUrl || attachment.url,
                    uploading: false,
                  }
                : attachment
            )
          );
        } catch {
          setAttachments((current) =>
            current.map((attachment) =>
              attachment.url === media.url ? { ...attachment, uploading: false, uploadError: true } : attachment
            )
          );
        }
      })
    );
  };

  const removeAttachment = (indexToRemove: number) => {
    setAttachments((current) => {
      const target = current[indexToRemove];
      if (target) URL.revokeObjectURL(target.url);
      return current.filter((_, index) => index !== indexToRemove);
    });
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: '#feed-editor-toolbar',
        handlers: {
          link: handleLink,
          image: handleImage,
          video: handleVideo,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [handleImage, handleLink, handleVideo]
  );

  const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'align', 'link', 'code', 'image', 'video'];

  const handlePost = async () => {
    const postTitle = composerTitle.trim();
    const postContent = composerValue.trim();
    const isUploadingMedia = attachments.some((attachment) => attachment.uploading);
    const failedMedia = attachments.some((attachment) => attachment.uploadError);
    const missingUploadedFileId = attachments.some((attachment) => !attachment.fileId);
    if (!postTitle || !postContent || !accessToken || portalPostSaving || isUploadingMedia || failedMedia || missingUploadedFileId) return;
    const fileIds = attachments.map((a) => a.fileId).filter(Boolean) as string[];

    const formattedDate = formatPostDateTime();

    const newPost = {
      userImage: user?.Profile_Picture_URL__c || HEADER_FAVICON_LOGO,
      title: postTitle,
      date: formattedDate,
      mediaLogo: HEADER_FAVICON_LOGO,
      content: postContent,
      contentHtml: composerHtml,
      hashtags: (postContent.match(/#[\w]+/g) || []).join(' '),
      media: attachments,
      isMine: true,
      status: 'PENDING',
      isPendingApproval: true,
      isRejected: false,
    };

    setPortalPostSaving(true);
    try {
      await createPortalPost(
        {
          title: postTitle,
          content: postContent,
          ...(fileIds.length > 0 && { fileIds }),
          audience: 'ALL',
        },
        accessToken
      );

      setMyPosts((posts) => [newPost, ...posts]);
      handleCancelPost();
    } finally {
      setPortalPostSaving(false);
    }
  };

  const handleOpenPostMenu = (event: React.MouseEvent<HTMLElement>, item: FeedItem) => {
    event.stopPropagation();
    setPostMenuAnchor(event.currentTarget);
    setPostMenuTarget(item);
  };

  const handleClosePostMenu = () => {
    setPostMenuAnchor(null);
    setPostMenuTarget(null);
  };

  const handleOpenEditDialog = () => {
    if (!postMenuTarget) return;
    setEditTitle(postMenuTarget.title);
    setEditContent(postMenuTarget.content);
    setEditDialogOpen(true);
    handleClosePostMenu();
  };

  const handleEditPost = async () => {
    if (!postMenuTarget?.id || !accessToken || editSaving) return;
    const title = editTitle.trim();
    const content = editContent.trim();
    if (!title || !content) return;

    const fileIds = postMenuTarget.media.map((m) => m.fileId).filter(Boolean) as string[];

    setEditSaving(true);
    try {
      await updatePortalPost(postMenuTarget.id, {
        title,
        content,
        ...(fileIds.length > 0 && { fileIds }),
        audience: 'ALL',
      }, accessToken);

      const updatedId = postMenuTarget.id;
      const applyUpdate = (posts: FeedItem[]) =>
        posts.map((p) => (p.id === updatedId ? { ...p, title, content } : p));
      setMyPosts(applyUpdate);
      setPortalPosts(applyUpdate);
      setEditDialogOpen(false);
      enqueueSnackbar('Post updated successfully', { variant: 'success', autoHideDuration: 3000 });
    } catch {
      enqueueSnackbar('Failed to update post', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    if (!postMenuTarget) return;
    setDeleteTarget(postMenuTarget);
    setDeleteDialogOpen(true);
    handleClosePostMenu();
  };

  const handleDeletePost = async () => {
    if (!deleteTarget?.id || !accessToken || deleteInProgress) return;

    setDeleteInProgress(true);
    try {
      await deletePortalPost(deleteTarget.id, accessToken);

      const removedId = deleteTarget.id;
      const applyRemove = (posts: FeedItem[]) => posts.filter((p) => p.id !== removedId);
      setMyPosts(applyRemove);
      setPortalPosts(applyRemove);
      setDeleteDialogOpen(false);
      enqueueSnackbar('Post deleted', { variant: 'success', autoHideDuration: 3000 });
    } catch {
      enqueueSnackbar('Failed to delete post', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setDeleteInProgress(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleLike = async (postId: string) => {
    if (!accessToken) return;
    const current = likeStatus[postId] ?? { liked: false, count: 0 };
    const wasLiked = current.liked;
    setLikeStatus((prev) => ({
      ...prev,
      [postId]: { liked: !wasLiked, count: Math.max(0, current.count + (wasLiked ? -1 : 1)) },
    }));
    try {
      const res = wasLiked ? await unlikePost(postId, accessToken) : await likePost(postId, accessToken);
      const payload = res?.data ?? res;
      setLikeStatus((prev) => ({ ...prev, [postId]: { liked: payload?.liked ?? !wasLiked, count: payload?.count ?? current.count } }));
    } catch {
      setLikeStatus((prev) => ({ ...prev, [postId]: current }));
    }
  };

  const loadComments = async (postId: string, page: number) => {
    if (!accessToken || commentsLoadingById[postId]) return;
    setCommentsLoadingById((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await listComments(postId, page, 10, accessToken);
      const pageData = res?.data ?? res;
      const items: CommentItem[] = (pageData?.data ?? []).map((c: any) => ({
        id: c.id,
        authorId: c.authorId,
        authorUserName: c.authorUserName || 'User',
        authorProfileUrl: c.authorProfileUrl,
        content: c.content,
        createdAt: c.createdAt,
      }));
      setCommentsByPostId((prev) => ({
        ...prev,
        [postId]: page === 1 ? items : [...(prev[postId] ?? []), ...items],
      }));
      setCommentHasMoreById((prev) => ({ ...prev, [postId]: pageData?.hasNext ?? false }));
      setCommentPageById((prev) => ({ ...prev, [postId]: page + 1 }));
    } catch {
      // silently fail
    } finally {
      setCommentsLoadingById((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleToggleComments = (postId: string) => {
    setExpandedComments((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
        if (!commentsByPostId[postId]) loadComments(postId, 1);
      }
      return next;
    });
  };

  const handleAddComment = async (postId: string) => {
    const content = (commentInput[postId] ?? '').trim();
    if (!content || !accessToken || commentSubmittingById[postId]) return;
    setCommentSubmittingById((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await addComment(postId, content, accessToken);
      const c = res?.data ?? res;
      const newComment: CommentItem = {
        id: c.id,
        authorId: c.authorId,
        authorUserName: c.authorUserName || user?.applicantName?.split(' ')?.[0] || 'You',
        authorProfileUrl: c.authorProfileUrl || user?.Profile_Picture_URL__c,
        content: c.content,
        createdAt: c.createdAt,
      };
      setCommentsByPostId((prev) => ({ ...prev, [postId]: [newComment, ...(prev[postId] ?? [])] }));
      setCommentInput((prev) => ({ ...prev, [postId]: '' }));
    } catch {
      enqueueSnackbar('Failed to add comment', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setCommentSubmittingById((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    if (!accessToken) return;
    try {
      await deleteComment(postId, commentId, accessToken);
      setCommentsByPostId((prev) => ({
        ...prev,
        [postId]: (prev[postId] ?? []).filter((c) => c.id !== commentId),
      }));
    } catch {
      enqueueSnackbar('Failed to delete comment', { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const renderComposer = () => (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #E4E8EF',
        borderRadius: isMobile ? '14px' : '18px',
        boxShadow: '0 14px 34px rgba(31, 41, 55, 0.07)',
        px: { xs: 2, md: composerOpen ? 4 : 5 },
        py: { xs: 2, md: composerOpen ? 3.5 : 3 },
        mb: 3,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: composerOpen ? 'flex-start' : 'center' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 220 } }}>
          <Box
            component="img"
            src={userAvatar}
            alt={displayName}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid #EAF0F7',
              backgroundColor: '#F4F7FB',
            }}
          />
          <Box>
            <Typography sx={{ color: '#1F2430', fontSize: 17, fontWeight: 500, lineHeight: 1.2 }}>
              Post a feed
            </Typography>
            <Typography sx={{ color: '#737B89', fontSize: 13, mt: 0.35 }}>
              Share with the community
            </Typography>
          </Box>
        </Stack>
        <Box
          onClick={() => setComposerOpen(true)}
          sx={{
            flex: 1,
            minHeight: composerOpen ? 'auto' : 58,
            border: composerOpen ? 'none' : '1px solid #CED6E0',
            borderRadius: composerOpen ? 0 : '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: composerOpen ? 0 : 2.25,
            color: '#444B58',
            cursor: composerOpen ? 'default' : 'text',
            fontSize: 16,
            backgroundColor: composerOpen ? 'transparent' : '#FAFBFD',
            transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
            '&:hover': composerOpen
              ? {}
              : {
                  borderColor: '#7DA0CE',
                  backgroundColor: '#fff',
                  boxShadow: '0 8px 18px rgba(44, 96, 170, 0.08)',
                },
          }}
        >
          <Typography sx={{ color: '#444B58', fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            What&apos;s on your mind, {displayName}?
          </Typography>
          {!composerOpen && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Tooltip title="Add image">
                <IconButton size="small" sx={{ color: '#2C60AA', backgroundColor: '#EEF5FF', '&:hover': { backgroundColor: '#E2EEFF' } }}>
                  <ImageOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add video">
                <IconButton size="small" sx={{ color: '#356B55', backgroundColor: '#EEF8F3', '&:hover': { backgroundColor: '#E0F1E9' } }}>
                  <PlayCircleOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

      </Stack>

      {composerOpen && (
        <>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            hidden
            onChange={(event) => handleLocalMediaUpload(event, 'photo')}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo"
            multiple
            hidden
            onChange={(event) => handleLocalMediaUpload(event, 'video')}
          />
          <TextField
            fullWidth
            value={composerTitle}
            onChange={(event) => setComposerTitle(event.target.value.slice(0, 120))}
            placeholder="Enter post title"
            variant="outlined"
            inputProps={{ maxLength: 120 }}
            sx={{
              mt: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: '#fff',
                fontSize: 18,
                fontWeight: 500,
              },
              '& .MuiOutlinedInput-input': {
                py: 1.6,
              },
            }}
          />
          <Box
            sx={{
              mt: 3,
              border: '1px solid #CDD1D8',
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: '#fff',
              '& .ql-container': {
                border: 'none',
                fontFamily: 'inherit',
                fontSize: '16px',
                height: '100%',
              },
              '& .ql-editor': {
                minHeight: '100%',
                pt: '64px',
                px: '24px',
                pb: '24px',
                fontSize: '16px',
                color: '#151925',
              },
              '& .ql-editor.ql-blank::before': {
                top: '64px',
                left: '24px',
                color: '#8A9099',
                fontStyle: 'normal',
                fontSize: '16px',
              },
              '& .ql-editor *': {
                fontSize: '16px !important',
              },
              '& .ql-editor img, & .ql-editor iframe': {
                maxWidth: '100%',
                borderRadius: '10px',
              },
            }}
          >
            <Stack
              id="feed-editor-toolbar"
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                minHeight: 48,
                px: 2,
                backgroundColor: '#E7E8EC',
                overflowX: 'auto',
              }}
            >
              {editorTools.map(({ icon: ToolIcon, label, className, value }, index) => (
                <React.Fragment key={index}>
                  {(index === 4 || index === 7) && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}
                  <Tooltip title={label}>
                    <IconButton
                      className={className}
                      value={value}
                      size="small"
                      sx={{ color: '#888C94' }}
                    >
                      <ToolIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </React.Fragment>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Tooltip title="Insert link">
                <IconButton className="ql-link" size="small" sx={{ color: '#888C94' }}>
                  <LinkIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Inline code">
                <IconButton size="small" onClick={handleInsertCode} onMouseDown={(event) => event.preventDefault()} sx={{ color: '#888C94' }}>
                  <CodeIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Mention">
                <IconButton size="small" onClick={handleMention} onMouseDown={(event) => event.preventDefault()} sx={{ color: '#888C94' }}>
                  <AlternateEmailIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Box sx={{ position: 'relative' }}>
                <Tooltip title="Emoji">
                  <IconButton
                    size="small"
                    onClick={() => setShowEmojiPicker((value) => !value)}
                    onMouseDown={(event) => event.preventDefault()}
                    sx={{ color: '#888C94' }}
                  >
                    <InsertEmoticonIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {showEmojiPicker && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 36,
                      left: -80,
                      zIndex: 4,
                      width: 176,
                      p: 1,
                      border: '1px solid #D7DBE3',
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: 0.5,
                    }}
                  >
                    {emojiOptions.map((emoji) => (
                      <IconButton
                        key={emoji}
                        size="small"
                        onClick={() => handleEmoji(emoji)}
                        onMouseDown={(event) => event.preventDefault()}
                        sx={{ fontSize: 16 }}
                      >
                        {emoji}
                      </IconButton>
                    ))}
                  </Box>
                )}
              </Box>
              <Tooltip title="Insert image">
                <IconButton className="ql-image" size="small" sx={{ color: '#888C94' }}>
                  <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Insert video">
                <IconButton className="ql-video" size="small" sx={{ color: '#888C94' }}>
                  <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Box sx={{ position: 'relative', height: { xs: 170, md: 210 } }}>
              <Typography sx={{ position: 'absolute', top: 24, left: 24, fontSize: 16, color: '#151925', zIndex: 1 }}>
                Content {composerValue.length}/4000
              </Typography>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={composerHtml}
                onChange={(content, _delta, _source, editor) => syncComposer(content, editor)}
                onFocus={() => setComposerOpen(true)}
                modules={modules}
                formats={formats}
                placeholder="Write post content..."
                style={{ height: '100%' }}
              />
            </Box>
            {attachments.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                  p: 2,
                  borderTop: '1px solid #E1E4EA',
                }}
              >
                {attachments.map((attachment, index) => (
                  <Box
                    key={`${attachment.url}-${index}`}
                    sx={{
                      position: 'relative',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid #DEE3EA',
                      backgroundColor: '#F8FAFC',
                      minHeight: 120,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => removeAttachment(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        width: 28,
                        height: 28,
                        backgroundColor: 'rgba(15, 23, 42, 0.72)',
                        color: '#fff',
                        '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.88)' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    {attachment.type === 'photo' ? (
                      <Box
                        component="img"
                        src={attachment.url}
                        alt={attachment.name || `attachment-${index}`}
                        sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <video
                        src={attachment.url}
                        controls
                        style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    {(attachment.uploading || attachment.uploadError) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: 1,
                          backgroundColor: 'rgba(15, 23, 42, 0.55)',
                          color: '#fff',
                          zIndex: 1,
                        }}
                      >
                        {attachment.uploading && <CircularProgress size={26} sx={{ color: '#fff' }} />}
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                          {attachment.uploading ? 'Uploading...' : 'Upload failed'}
                        </Typography>
                      </Box>
                    )}
                    {attachment.name && (
                      <Typography
                        sx={{
                          px: 1.5,
                          py: 1,
                          fontSize: 14,
                          color: '#3F4652',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {attachment.name}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              onClick={handleCancelPost}
              sx={{
                minWidth: 138,
                borderRadius: '9px',
                backgroundColor: '#DADADA',
                color: '#050505',
                fontSize: 16,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#CFCFCF' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePost}
              disabled={
                !composerTitle.trim() ||
                !composerValue.trim() ||
                !accessToken ||
                portalPostSaving ||
                attachments.some((attachment) => attachment.uploading || attachment.uploadError || !attachment.fileId)
              }
              sx={{
                minWidth: 138,
                borderRadius: '9px',
                backgroundColor: '#2C60AA',
                fontSize: 16,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#24579E', boxShadow: 'none' },
                '&.Mui-disabled': { backgroundColor: '#9EB4D4', color: '#fff' },
              }}
            >
              {portalPostSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Post'}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );

  const renderSpotlight = () => (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #2D66B5 0%, #4D86CF 100%)',
        borderRadius: '12px',
        p: { xs: 2, md: 3 },
        width: { xs: '100%', md: '86%' },
        mx: 'auto',
        mb: 3,
      }}
    >
      <Typography sx={{ fontWeight: 500, color: '#fff', textAlign: 'left', fontSize: 16, mb: 2 }}>
        In the spotlight
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 7.2',
          minHeight: { xs: 210, md: 320 },
          overflow: 'hidden',
          backgroundColor: '#D2D4D7',
        }}
      >
        <iframe
          width="100%"
          height="100%"
          src={youtubeVideoURL?.youtube_Video_URL__c || ''}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          style={{ border: 0, display: 'block' }}
        />
      </Box>
      {youtubeVideoURL?.youtube_Title__c && (
        <Typography sx={{ fontWeight: 500, color: '#fff', mt: 2, textAlign: 'left' }}>
          {youtubeVideoURL.youtube_Title__c}
        </Typography>
      )}
      {youtubeVideoURL?.youtube_Description__c && (
        <Typography sx={{ color: '#fff', textAlign: 'left' }}>{youtubeVideoURL.youtube_Description__c}</Typography>
      )}
    </Box>
  );

  const renderFeedItem = (item: FeedItem, index: number) => {
    const showPendingApproval = activeTab === 2 && item.isPendingApproval;
    const showRejected = activeTab === 2 && item.isRejected;
    const statusStyles = showRejected
      ? {
          border: '1px solid #F0A6A6',
          backgroundColor: '#FFF8F8',
          badgeBg: '#FFE4E4',
          badgeColor: '#9D1C1C',
          noticeBg: '#FFF0F0',
          noticeBorder: '1px solid #F3B7B7',
          noticeColor: '#7B1E1E',
          badgeIcon: <CancelOutlinedIcon sx={{ fontSize: 15 }} />,
          noticeIcon: <CancelOutlinedIcon sx={{ fontSize: 18 }} />,
          badgeText: 'Rejected',
          noticeText: 'This post was rejected. Please review the content and create a new post if needed.',
        }
      : showPendingApproval
      ? {
          border: '1px solid #F0C36A',
          backgroundColor: '#FFFDF7',
          badgeBg: '#FFF1CC',
          badgeColor: '#7A4D00',
          noticeBg: '#FFF6DF',
          noticeBorder: '1px solid #F3D38A',
          noticeColor: '#654300',
          badgeIcon: <PendingActionsIcon sx={{ fontSize: 15 }} />,
          noticeIcon: <PendingActionsIcon sx={{ fontSize: 18 }} />,
          badgeText: 'Pending approval',
          noticeText: 'This post is pending approval. It will be visible to others after review.',
        }
      : null;

    return (
    <Box
      key={`${item.content}-${index}`}
      sx={{
        border: statusStyles?.border || '1px solid #E6E6E8',
        borderRadius: '12px',
        mb: 2,
        fontSize: 14,
        backgroundColor: statusStyles?.backgroundColor || 'white',
        p: { xs: 2, md: 2.5 },
        width: { xs: '100%', md: '86%' },
        mx: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2.5}>
        <Box sx={{ flex: '0 0 auto' }}>
          <Box
            component="img"
            src={item.userImage}
            alt="User"
            sx={{ width: { xs: 44, md: 56 }, height: { xs: 44, md: 56 }, borderRadius: '50%', objectFit: 'cover' }}
          />
        </Box>
        <Box sx={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{item.title}</Typography>
            {statusStyles && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: '999px',
                  backgroundColor: statusStyles.badgeBg,
                  color: statusStyles.badgeColor,
                  width: 'fit-content',
                }}
              >
                {statusStyles.badgeIcon}
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{statusStyles.badgeText}</Typography>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, color: '#6F7682' }}>
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 400 }}>{item.date}</Typography>
          </Stack>
        </Box>
        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {activeTab === 2 && item.id && (
            <Tooltip title="Post options">
              <IconButton
                size="small"
                onClick={(e) => handleOpenPostMenu(e, item)}
                sx={{ color: '#6F7682', '&:hover': { backgroundColor: '#F0F2F6' } }}
              >
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <Box
            component="img"
            src={item.mediaLogo}
            alt="Media"
            sx={{ width: { xs: 34, md: 46 }, height: { xs: 34, md: 46 }, borderRadius: '50%' }}
          />
        </Box>
      </Stack>

      {statusStyles && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: '8px',
            backgroundColor: statusStyles.noticeBg,
            border: statusStyles.noticeBorder,
            color: statusStyles.noticeColor,
          }}
        >
          {statusStyles.noticeIcon}
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {statusStyles.noticeText}
          </Typography>
        </Stack>
      )}

      <Typography
        mt={2.5}
        component="div"
        textAlign="left"
        fontSize={14}
        sx={{ '& *': { fontSize: '16px !important' } }}
        dangerouslySetInnerHTML={{ __html: item.contentHtml || linkify(item.content) }}
      />
      {item.hashtags && (
        <Typography mt={2} textAlign="left" color="primary.main" fontSize={14}>
          {item.hashtags}
        </Typography>
      )}

      {item.media.length > 0 && <MediaCarousel media={item.media} />}

      {item.id && (
        <>
          <Divider sx={{ mt: 2, mb: 0.25 }} />
          <Stack direction="row" spacing={0} alignItems="center">
            <Button
              size="small"
              disabled={item.isPendingApproval || item.isRejected}
              startIcon={likeStatus[item.id]?.liked
                ? <FavoriteIcon sx={{ fontSize: '17px !important', color: '#E53935' }} />
                : <FavoriteBorderIcon sx={{ fontSize: '17px !important' }} />}
              onClick={() => handleToggleLike(item.id!)}
              sx={{
                textTransform: 'none',
                color: likeStatus[item.id]?.liked ? '#E53935' : '#5C6472',
                fontSize: 13.5,
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.9,
                flex: 1,
                justifyContent: 'center',
                '&:hover': { backgroundColor: likeStatus[item.id]?.liked ? '#FFF0F0' : '#F2F4F7' },
                '&.Mui-disabled': { color: '#B0B7C3' },
              }}
            >
              {(likeStatus[item.id]?.count ?? 0) > 0
                ? `${likeStatus[item.id]?.count} ${(likeStatus[item.id]?.count ?? 0) === 1 ? 'Like' : 'Likes'}`
                : 'Like'}
            </Button>
            <Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />
            <Button
              size="small"
              disabled={item.isPendingApproval || item.isRejected}
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '17px !important' }} />}
              onClick={() => handleToggleComments(item.id!)}
              sx={{
                textTransform: 'none',
                color: expandedComments.has(item.id!) ? '#2C60AA' : '#5C6472',
                fontSize: 13.5,
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.9,
                flex: 1,
                justifyContent: 'center',
                '&:hover': { backgroundColor: expandedComments.has(item.id!) ? '#EEF5FF' : '#F2F4F7' },
                '&.Mui-disabled': { color: '#B0B7C3' },
              }}
            >
              Comment
            </Button>
          </Stack>

          {expandedComments.has(item.id!) && !item.isPendingApproval && !item.isRejected && (
            <Box sx={{ mt: 1.5 }}>
              {/* Comment list */}
              {commentsLoadingById[item.id!] && !(commentsByPostId[item.id!]?.length) ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <>
                  {commentHasMoreById[item.id!] && (
                    <Typography
                      onClick={() => !commentsLoadingById[item.id!] && loadComments(item.id!, commentPageById[item.id!] ?? 2)}
                      sx={{ fontSize: 13, fontWeight: 600, color: '#737B89', cursor: 'pointer', mb: 1.5, '&:hover': { color: '#3D4452' } }}
                    >
                      {commentsLoadingById[item.id!] ? 'Loading…' : 'View previous comments'}
                    </Typography>
                  )}

                  {(commentsByPostId[item.id!] ?? []).map((comment) => (
                    <Stack key={comment.id} direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.75 }}>
                      <Box
                        component="img"
                        src={comment.authorProfileUrl || HEADER_FAVICON_LOGO}
                        alt={comment.authorUserName}
                        sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, mt: 0.25 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, color: '#111', lineHeight: 1.5, textAlign: 'left', wordBreak: 'break-word' }}>
                          <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>{comment.authorUserName}</Box>
                          {comment.content}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.4 }}>
                          <Typography sx={{ fontSize: 12, color: '#8E8E8E', fontWeight: 400 }}>
                            {formatPostDateTime(comment.createdAt)}
                          </Typography>
                          {comment.authorId === user?.applicantId && (
                            <Typography
                              component="span"
                              onClick={() => handleDeleteComment(item.id!, comment.id)}
                              sx={{ fontSize: 12, color: '#8E8E8E', fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#E53935' } }}
                            >
                              Delete
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <FavoriteBorderIcon sx={{ fontSize: 12, color: '#8E8E8E', flexShrink: 0, mt: 0.6, cursor: 'pointer' }} />
                    </Stack>
                  ))}
                </>
              )}

              {/* Instagram-style comment input */}
              <Divider sx={{ mb: 1.5 }} />
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  component="img"
                  src={userAvatar}
                  alt="me"
                  sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                />
                <Box
                  component="textarea"
                  placeholder="Add a comment…"
                  value={commentInput[item.id!] ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCommentInput((prev) => ({ ...prev, [item.id!]: e.target.value.slice(0, 2000) }))
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(item.id!); }
                  }}
                  rows={1}
                  sx={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 14,
                    color: '#111',
                    resize: 'none',
                    fontFamily: 'inherit',
                    lineHeight: 1.5,
                    py: 0.5,
                    '&::placeholder': { color: '#8E8E8E' },
                  }}
                />
                {commentInput[item.id!]?.trim() && (
                  <Typography
                    component="span"
                    onClick={() => !commentSubmittingById[item.id!] && handleAddComment(item.id!)}
                    sx={{ fontSize: 14, fontWeight: 700, color: '#0095F6', cursor: 'pointer', flexShrink: 0, '&:hover': { color: '#00376B' } }}
                  >
                    {commentSubmittingById[item.id!] ? <CircularProgress size={14} sx={{ color: '#0095F6' }} /> : 'Post'}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
    );
  };

  return (
    <Box
      sx={{
        height: isMobile ? '80vh' : '84vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {!hideComposer && renderComposer()}

      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #E4E8EF',
          borderRadius: isMobile ? '14px' : '18px',
          boxShadow: '0 12px 30px rgba(31, 41, 55, 0.05)',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            minHeight: 76,
            borderBottom: '1px solid #E6EAF0',
            px: { xs: 2, md: 5 },
            py: 1.5,
            backgroundColor: '#FBFCFE',
            '& .MuiTab-root': {
              minHeight: 44,
              minWidth: { xs: 104, md: 160 },
              textTransform: 'none',
              color: '#596171',
              fontSize: 16,
              fontWeight: 500,
              borderRadius: '12px',
              mr: { xs: 0.75, md: 1.25 },
              transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
              '&:hover': {
                backgroundColor: '#F0F4F8',
                color: '#263244',
              },
            },
            '& .Mui-selected': {
              color: '#143B6E !important',
              backgroundColor: '#EAF2FC',
              boxShadow: 'inset 0 0 0 1px #C8DAF0',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab label="Social Feeds" />
          <Tab label="Feeds" />
          <Tab label="My Posts" />
        </Tabs>

        <Box
          onScroll={handlePostsScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 2, md: 0 },
            py: { xs: 2, md: 4 },
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: '#F0F2F6' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#D8DEE9', borderRadius: 10 },
          }}
        >
          {visiblePosts.length > 0 ? (
            <>
              {activeTab === 0 && renderSpotlight()}
              {visiblePosts.map(renderFeedItem)}
              {activeTab === 1 && portalPostsLoading && (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {activeTab === 2 && myPostsLoading && (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ py: 8, textAlign: 'center', color: '#69707D' }}>
              {(activeTab === 1 && portalPostsLoading) || (activeTab === 2 && myPostsLoading) ? (
                <CircularProgress size={28} />
              ) : (
                <Typography fontSize={16}>No posts found.</Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Post options menu */}
      <Menu
        anchorEl={postMenuAnchor}
        open={Boolean(postMenuAnchor)}
        onClose={handleClosePostMenu}
        PaperProps={{
          sx: { boxShadow: '0 8px 20px rgba(15,23,42,0.12)', borderRadius: '10px', minWidth: 160 },
        }}
      >
        <MenuItem onClick={handleOpenEditDialog} sx={{ gap: 1.5, fontSize: 14, py: 1.25 }}>
          <EditOutlinedIcon sx={{ fontSize: 18, color: '#2C60AA' }} />
          Edit post
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ gap: 1.5, fontSize: 14, py: 1.25, color: '#C0392B' }}>
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          Delete post
        </MenuItem>
      </Menu>

      {/* Edit dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editSaving && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600, pb: 1 }}>Edit Post</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value.slice(0, 120))}
            inputProps={{ maxLength: 120 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          <TextField
            fullWidth
            label="Content"
            multiline
            minRows={5}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value.slice(0, 4000))}
            inputProps={{ maxLength: 4000 }}
            helperText={`${editContent.length} / 4000`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={editSaving}
            sx={{ textTransform: 'none', color: '#555', borderRadius: '9px', minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditPost}
            disabled={!editTitle.trim() || !editContent.trim() || editSaving}
            sx={{
              textTransform: 'none',
              borderRadius: '9px',
              minWidth: 100,
              backgroundColor: '#2C60AA',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#24579E', boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: '#9EB4D4', color: '#fff' },
            }}
          >
            {editSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteInProgress && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Delete Post</DialogTitle>
        <DialogContent>
          <Typography fontSize={15} color="#444">
            Are you sure you want to delete this post? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteInProgress}
            sx={{ textTransform: 'none', color: '#555', borderRadius: '9px', minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeletePost}
            disabled={deleteInProgress}
            sx={{
              textTransform: 'none',
              borderRadius: '9px',
              minWidth: 100,
              backgroundColor: '#C0392B',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#A93226', boxShadow: 'none' },
            }}
          >
            {deleteInProgress ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Feed;
